import React, { useContext } from 'react';
import { FormattedMessage } from 'react-intl';
import { OutlierTimeOption } from '@amzn/aet-dynamo-kit';
import Text from '@amzn/meridian/text';
import Row from '@amzn/meridian/row';
import Column from '@amzn/meridian/column';
import Box from '@amzn/meridian/box';
import ExpandableCard from '../../../commons/ExpandableCard';
import { LinearGaugeWithSubtitle } from '../../../commons/LinearGaugeWithSubtitle';
import UserBadgeThumbnails from '../../Dashboard/DashboardComponents/UserBadgeThumbnails';
import { OMOutlierSummaryStats } from '../types/types';
import { StateContext } from '../../../GlobalStateStore';

interface OMPendingOutliers {
  totalOutliers: number;
  pendingOutliers: number;
  reviewedOutliers: number;
  totalTMs: number;
  tmsWithOutliers: number;
  tmsWithRepeatOutliers: number;
  requiredRemaining: number;
  totalRequired: number;
}

interface OMPendingOutliersCardProps {
  metrics: OMPendingOutliers;
  tmStats: OMOutlierSummaryStats[];
  viewDataBy: OutlierTimeOption;
}

export const OMPendingOutliersCard: React.FC<OMPendingOutliersCardProps> = ({
  metrics,
  tmStats,
  viewDataBy,
}) => {
  const globalState = useContext(StateContext);
  const { inspireOptions } = globalState.state;
  const { isTm, isOm, requesterUserID, requesterUsername } =
    inspireOptions.data;

  const header = (
    <Box spacingInset='400 300 300 300'>
      <Row alignmentHorizontal='justify' alignmentVertical='bottom'>
        <Column spacing='small'>
          <Text type='h500' data-testid='pending-outliers-count'>
            {metrics.requiredRemaining}
          </Text>
          <Text type='h50'>
            <FormattedMessage
              id='cs_perfportal_required_remaining'
              defaultMessage='Required remaining'
            />
          </Text>
        </Column>
        <Box>
          <LinearGaugeWithSubtitle
            total={metrics.totalRequired}
            value={metrics.totalRequired - metrics.requiredRemaining}
            topSubtitle={
              <FormattedMessage
                id='cs_perfportal_outliers_for_selected_dates'
                defaultMessage='For selected dates'
              />
            }
            subtitle={
              viewDataBy === OutlierTimeOption.WEEKLY ? (
                <FormattedMessage
                  id='cs_perfportal_required_reviews_count'
                  defaultMessage='{completed}/{total} required completed'
                  values={{
                    completed:
                      metrics.totalRequired - metrics.requiredRemaining,
                    total: metrics.totalRequired,
                  }}
                />
              ) : (
                <FormattedMessage
                  id='cs_perfportal_required_reviews_count_these_weeks'
                  defaultMessage='{completed}/{total} required reviews completed these weeks'
                  values={{
                    completed:
                      metrics.totalRequired - metrics.requiredRemaining,
                    total: metrics.totalRequired,
                  }}
                />
              )
            }
          />
        </Box>
      </Row>
    </Box>
  );

  return (
    <ExpandableCard
      title={
        <FormattedMessage
          id='cs_perfportal_outlier_reviews_this_week'
          defaultMessage='Outlier reviews this week'
        />
      }
      header={header}
      expandedContent={<OMPendingOutliersExpandedCard tmStats={tmStats} />}
      cardSpacingInset='350 450 450 450'
      titleRowSpacing='300 300 200 400'
      clickstreamTags={{
        'data-csa-c-type': 'link',
        'data-csa-c-slot-id': `om-outlier-management-pending-outlier-link-slot`,
        'data-csa-c-tag1': 'Coaching',
        'data-csa-c-tag2': 'Outlier Metrics',
        'data-csa-c-tag3': `Pending Outlier`,
        'data-csa-c-profile-id': requesterUserID,
        'data-csa-c-username': requesterUsername,
        'data-csa-c-is-TM': isTm,
        'data-csa-c-is-OM': isOm,
      }}
    />
  );
};

interface OMPendingOutliersExpandedCardProps {
  tmStats: OMOutlierSummaryStats[];
}

const OMPendingOutliersExpandedCard: React.FC<
  OMPendingOutliersExpandedCardProps
> = ({ tmStats }) => {
  const expandedContent = (
    <Column spacing='small' spacingInset='none 200 none none'>
      <Row
        alignmentHorizontal='justify'
        alignmentVertical='center'
        spacing='small'
        width='100%'
      >
        <Text type='b200' color='secondary'>
          <FormattedMessage id='cs_perfportal_tm' defaultMessage='TM' />
        </Text>
        <Text type='b200' color='secondary'>
          <FormattedMessage
            id='cs_perfportal_remaining_reviews_required'
            defaultMessage='Remaining reviews required'
          />
        </Text>
      </Row>

      {tmStats.map((tmOutlierInfo, index) => (
        <Row
          key={index}
          alignmentHorizontal='start'
          alignmentVertical='center'
          spacing='small'
          spacingInset='200 none'
          width='100%'
        >
          <UserBadgeThumbnails usernames={[tmOutlierInfo.tmAlias]} />
          <Column spacingInset='100 100 100 450' spacing='200' width={175}>
            <Text type='b300'>{tmOutlierInfo.tmAlias}</Text>
            <Text type='b100' color='secondary'>
              <FormattedMessage
                id='cs_perfportal_outlier_om_associates_num'
                defaultMessage='{numAssociates} Associates with outliers'
                values={{ numAssociates: tmOutlierInfo.numAssociates }}
              />
            </Text>
          </Column>
          <Row spacing='100' alignmentVertical='bottom' spacingInset='None 500'>
            <Text type='h500' data-testid='tm-pending-outliers-count'>
              {tmOutlierInfo.requiredRemaining || 0}
            </Text>
            <Text type='h50'>/</Text>
            <Text type='h50'> {tmOutlierInfo.totalRequired || 0}</Text>
          </Row>
        </Row>
      ))}
    </Column>
  );

  return expandedContent;
};

export default OMPendingOutliersCard;
